import React, { useState, useEffect } from "react";
import api from "../api/homeapi";
import { StaffFrame } from "./DashboardShared";
import { useSettings } from "../context/SettingsContext";
import { ImageIcon, HomeIcon, MoneyIcon, PinIcon, CheckIcon, UploadIcon, FileTextIcon } from "../components/Icons";

const C = { green:"#006B3C", greenLt:"#E8F5EE", grayBd:"#E0E0E0", text:"#1A1A1A", textSub:"#666" };

const s = {
  label: { display:"block", fontSize:12, fontWeight:700, color:C.textSub, marginBottom:6, textTransform:"uppercase" },
  input: { width:"100%", border:`1.5px solid ${C.grayBd}`, borderRadius:10, padding:"10px 12px", fontSize:14, fontFamily:"inherit" },
  card: { background:"#fff", borderRadius:14, padding:28, border:`1px solid ${C.grayBd}`, marginBottom:20 },
};

export default function AdminSettings() {
  const { refresh: refreshGlobalSettings } = useSettings();
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/settings")
      .then(r => setForm({ ...r.data, tariffs: r.data.tariffs || [] }))
      .catch(() => setError("Impossible de charger les paramètres actuels."))
      .finally(() => setLoading(false));
  }, []);

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleLogoFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 300 * 1024) {
      setError("Image trop lourde (max ~300 Ko). Compressez-la ou utilisez une URL externe.");
      return;
    }
    setError("");
    const reader = new FileReader();
    reader.onload = () => set("logoUrl", reader.result);
    reader.readAsDataURL(file);
  };

  const setTariff = (i, key, v) => setForm(p => {
    const tariffs = [...p.tariffs];
    tariffs[i] = { ...tariffs[i], [key]: v };
    return { ...p, tariffs };
  });
  const addTariff = () => setForm(p => ({ ...p, tariffs: [...p.tariffs, { from: "", to: "", eco: null, pre: null, vip: null }] }));
  const removeTariff = (i) => setForm(p => ({ ...p, tariffs: p.tariffs.filter((_, idx) => idx !== i) }));

  const handleSave = async () => {
    setSaving(true); setError("");
    try {
      await api.put("/api/admin/settings", form);
      setSaved(true);
      await refreshGlobalSettings(); // applique immédiatement les nouvelles couleurs/textes sur tout le site
      setTimeout(() => setSaved(false), 2500);
    } catch {
      setError("Échec de l'enregistrement. Vérifiez votre connexion et réessayez.");
    } finally {
      setSaving(false);
    }
  };

  if (loading || !form) {
    return <StaffFrame><div style={{ padding:28 }}>Chargement des paramètres…</div></StaffFrame>;
  }

  return (
    <StaffFrame>
      <div style={{ padding:28, maxWidth:760 }}>
        <h1 style={{ margin:"0 0 4px", fontSize:22, fontWeight:800 }}>Paramètres</h1>
        <p style={{ margin:"0 0 28px", color:C.textSub, fontSize:13 }}>
          Tout ce qui est modifié ici s'applique immédiatement sur le site public (accueil, tarifs, mentions légales, bande d'annonce).
        </p>

        {error && <div className="alert alert-danger">{error}</div>}
        {saved && <div className="alert alert-success">✅ Paramètres enregistrés.</div>}

        {/* Identité visuelle */}
        <div style={s.card}>
          <h2 style={{ margin:"0 0 20px", fontSize:16, fontWeight:700, display:"flex", alignItems:"center", gap:8 }}><ImageIcon size={17}/> Identité visuelle</h2>
          <div style={{ marginBottom:16 }}>
            <label style={s.label}>Logo</label>
            <div style={{ display:"flex", alignItems:"center", gap:14, marginBottom:10 }}>
              {form.logoUrl ? (
                <img src={form.logoUrl} alt="Logo" style={{ height:48, maxWidth:140, objectFit:"contain", border:`1.5px solid ${C.grayBd}`, borderRadius:8, background:"#fff", padding:4 }} />
              ) : (
                <div style={{ height:48, width:96, display:"flex", alignItems:"center", justifyContent:"center", border:`1.5px dashed ${C.grayBd}`, borderRadius:8, fontSize:11, color:C.textSub }}>
                  Aucun logo
                </div>
              )}
              <label style={{ background:C.greenLt, color:C.green, border:"none", borderRadius:8, padding:"8px 16px", fontWeight:700, fontSize:13, cursor:"pointer" }}>
                <UploadIcon size={14}/> Téléverser une image
                <input type="file" accept="image/*" style={{ display:"none" }} onChange={handleLogoFile} />
              </label>
              {form.logoUrl && (
                <button onClick={() => set("logoUrl", "")} style={{ background:"#FEE2E2", color:"#B91C1C", border:"none", borderRadius:8, padding:"8px 14px", fontWeight:700, fontSize:13, cursor:"pointer" }}>
                  Retirer
                </button>
              )}
            </div>
            <label style={s.label}>ou URL du logo</label>
            <input style={s.input} placeholder="https://…/logo.png" value={form.logoUrl && !form.logoUrl.startsWith("data:") ? form.logoUrl : ""} onChange={e => set("logoUrl", e.target.value)} />
            <p style={{ fontSize:11, color:C.textSub, marginTop:4 }}>
              Laissez vide pour garder le logo texte "SETRAG" par défaut. Dimensions recommandées : environ <strong>200 × 60 px</strong> (format horizontal, fond transparent en PNG ou SVG de préférence) pour bien s'adapter à la barre de navigation et à la barre latérale. L'image est enregistrée directement dans les paramètres (pas de serveur de fichiers séparé) - préférez une image légère (idéalement &lt; 100 Ko).
            </p>
          </div>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:16 }}>
            <div>
              <label style={s.label}>Couleur principale</label>
              <div style={{ display:"flex", gap:8 }}>
                <input type="color" value={form.primaryColor || "#006B3C"} onChange={e => set("primaryColor", e.target.value)} style={{ width:44, height:40, border:`1.5px solid ${C.grayBd}`, borderRadius:8, padding:2 }} />
                <input style={s.input} value={form.primaryColor || ""} onChange={e => set("primaryColor", e.target.value)} />
              </div>
            </div>
            <div>
              <label style={s.label}>Couleur secondaire</label>
              <div style={{ display:"flex", gap:8 }}>
                <input type="color" value={form.secondaryColor || "#B8933A"} onChange={e => set("secondaryColor", e.target.value)} style={{ width:44, height:40, border:`1.5px solid ${C.grayBd}`, borderRadius:8, padding:2 }} />
                <input style={s.input} value={form.secondaryColor || ""} onChange={e => set("secondaryColor", e.target.value)} />
              </div>
            </div>
          </div>
          <p style={{ fontSize:11, color:"#B45309", marginTop:12 }}>
            ⚠️ Ces couleurs pilotent la bande d'annonce et les nouvelles pages (variables CSS globales). La majorité des écrans existants ont leurs couleurs codées en dur et ne suivent pas encore ce réglage - c'est une évolution en cours, pas un bug.
          </p>
        </div>

        {/* Page d'accueil */}
        <div style={s.card}>
          <h2 style={{ margin:"0 0 20px", fontSize:16, fontWeight:700, display:"flex", alignItems:"center", gap:8 }}><HomeIcon size={17}/> Page d'accueil</h2>
          <div style={{ marginBottom:16 }}>
            <label style={s.label}>Titre principal</label>
            <input style={s.input} value={form.heroTitle || ""} onChange={e => set("heroTitle", e.target.value)} />
          </div>
          <div>
            <label style={s.label}>Sous-titre</label>
            <textarea style={{ ...s.input, height:70, resize:"vertical" }} value={form.heroSubtitle || ""} onChange={e => set("heroSubtitle", e.target.value)} />
          </div>
        </div>

        {/* Tarifs */}
        <div style={s.card}>
          <h2 style={{ margin:"0 0 4px", fontSize:16, fontWeight:700, display:"flex", alignItems:"center", gap:8 }}><MoneyIcon size={17}/> Grille tarifaire (page Tarifs)</h2>
          <p style={{ fontSize:12, color:C.textSub, margin:"0 0 16px" }}>Prix par trajet et par classe (FCFA). Laissez vide s'il n'y a pas de VIP sur ce trajet.</p>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 90px 90px 90px auto", gap:8, marginBottom:8, fontSize:11, fontWeight:700, color:C.textSub, textTransform:"uppercase" }}>
            <span>Départ</span><span>Arrivée</span><span>Éco</span><span>1ère</span><span>VIP</span><span></span>
          </div>
          {form.tariffs.map((t, i) => (
            <div key={i} style={{ display:"grid", gridTemplateColumns:"1fr 1fr 90px 90px 90px auto", gap:8, marginBottom:8, alignItems:"center" }}>
              <input style={s.input} placeholder="Libreville" value={t.from || ""} onChange={e => setTariff(i, "from", e.target.value)} />
              <input style={s.input} placeholder="Franceville" value={t.to || ""} onChange={e => setTariff(i, "to", e.target.value)} />
              <input style={s.input} type="number" value={t.eco ?? ""} onChange={e => setTariff(i, "eco", e.target.value ? Number(e.target.value) : null)} />
              <input style={s.input} type="number" value={t.pre ?? ""} onChange={e => setTariff(i, "pre", e.target.value ? Number(e.target.value) : null)} />
              <input style={s.input} type="number" value={t.vip ?? ""} onChange={e => setTariff(i, "vip", e.target.value ? Number(e.target.value) : null)} />
              <button onClick={() => removeTariff(i)} style={{ background:"#FEE2E2", color:"#B91C1C", border:"none", borderRadius:8, padding:"8px 12px", cursor:"pointer", fontWeight:700 }}>✕</button>
            </div>
          ))}
          <button onClick={addTariff} style={{ background:C.greenLt, color:C.green, border:"none", borderRadius:8, padding:"8px 16px", fontWeight:700, fontSize:13, cursor:"pointer", marginTop:8 }}>
            ➕ Ajouter un trajet
          </button>
        </div>

        {/* Bande d'annonce */}
        <div style={s.card}>
          <h2 style={{ margin:"0 0 20px", fontSize:16, fontWeight:700, display:"flex", alignItems:"center", gap:8 }}><PinIcon size={17}/> Bande d'annonce (toutes les pages)</h2>
          <label style={{ display:"flex", alignItems:"center", gap:8, fontSize:13, marginBottom:14 }}>
            <input type="checkbox" checked={form.bannerActive} onChange={e => set("bannerActive", e.target.checked)} />
            Afficher la bande d'annonce sur le site
          </label>
          <input style={s.input} placeholder="Ex : Trafic perturbé entre Libreville et Franceville le 20 août." value={form.bannerText || ""} onChange={e => set("bannerText", e.target.value)} />
        </div>

        {/* Mentions légales */}
        <div style={s.card}>
          <h2 style={{ margin:"0 0 20px", fontSize:16, fontWeight:700, display:"flex", alignItems:"center", gap:8 }}><FileTextIcon size={17}/> Mentions légales</h2>
          <div style={{ marginBottom:16 }}>
            <label style={s.label}>Politique de confidentialité</label>
            <textarea style={{ ...s.input, height:160, resize:"vertical", fontFamily:"monospace", fontSize:13 }} value={form.privacyPolicy || ""} onChange={e => set("privacyPolicy", e.target.value)} />
          </div>
          <div>
            <label style={s.label}>Conditions générales d'utilisation</label>
            <textarea style={{ ...s.input, height:160, resize:"vertical", fontFamily:"monospace", fontSize:13 }} value={form.termsOfService || ""} onChange={e => set("termsOfService", e.target.value)} />
          </div>
        </div>

        <button onClick={handleSave} disabled={saving}
          style={{ width:"100%", background:C.green, color:"#fff", border:"none", borderRadius:12, padding:16, fontWeight:700, fontSize:15, cursor:"pointer", opacity:saving?0.7:1 }}>
          {saving ? "Enregistrement…" : <><CheckIcon size={16}/> Enregistrer les paramètres</>}
        </button>
      </div>
    </StaffFrame>
  );
}
